import React, { useRef, useState } from 'react';
import { AnimatePresence, motion, useScroll, useTransform } from 'framer-motion';
import Icon from '../components/Icon';
import { PageHero, SectionHead, ArrowButton, StatsRow, CTABand } from '../components/ui';
import VelocityMarquee from '../motion/VelocityMarquee';
import { Reveal, Stagger, StaggerItem, EASE } from '../motion/Reveal';
import { CAPABILITIES, VALUES, WHY_PMC, TICKER } from '../data/site';
import { ContractingRows, EngagementModels } from '../components/Contracting';

function StackCard({ c, i, total, progress }) {
  const target = 1 - (total - i) * 0.035;
  const scale = useTransform(progress, [i / total, 1], [1, target]);
  const shade = useTransform(progress, [i / total, Math.min(1, (i + 1) / total)], [0, 0.18]);
  return (
    <div className="stack-card" style={{ top: `calc(110px + ${i * 22}px)` }}>
      <motion.div className="stack-inner" style={{ scale }}>
        <div className="stack-media">
          <img src={c.image} alt={c.title} loading="lazy" />
          <span className="num">0{i + 1} / 0{total}</span>
        </div>
        <div className="stack-body">
          <div className="icon-box"><Icon name={c.icon} size={24} /></div>
          <h3>{c.title}</h3>
          <p>{c.description}</p>
          <div className="stack-lists">
            <div><h4>Scope of service</h4><ul className="check-list">{c.details.map((d) => <li key={d}><Icon name="check" size={15} />{d}</li>)}</ul></div>
            <div><h4>What you get</h4><ul className="check-list">{c.expanded.map((d) => <li key={d}><Icon name="checkCircle" size={15} />{d}</li>)}</ul></div>
          </div>
          <div className="tag-row">{c.keywords.map((k) => <span className="tag" key={k}>{k}</span>)}</div>
        </div>
        <motion.span className="stack-shade" style={{ opacity: shade }} aria-hidden="true" />
      </motion.div>
    </div>
  );
}

export default function Capabilities() {
  const stackRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: stackRef, offset: ['start start', 'end end'] });
  const [open, setOpen] = useState(0);


  return (
    <>
      <PageHero
        title="PMC &amp; contracting,|*one accountable team*"
        lead="Six PMC pillars that govern your project end to end — and a contracting division that builds it. Choose one, or let one team handle both."
        images={['/projects/mahendhran-residence-1.webp', 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=560&h=560&fit=crop&q=68&auto=format']}>
        <ArrowButton href="#pillars">PMC services</ArrowButton>
        <ArrowButton href="#contracting" variant="outline">Contracting services</ArrowButton>
      </PageHero>

      <VelocityMarquee items={TICKER.slice(4)} />

      {/* Core Companies Start */}
      <section className="section" id="pillars" style={{ scrollMarginTop: 40 }}>
        <div className="container">
          <SectionHead center num="01" kicker="PMC services" title="Six pillars across the|*entire* lifecycle" lead="Scroll through each pillar — every one is a full service on its own, and together they cover your whole build." />
          <div className="stack" ref={stackRef}>
            {CAPABILITIES.map((c, i) => <StackCard key={c.title} c={c} i={i} total={CAPABILITIES.length} progress={scrollYProgress} />)}
          </div>
        </div>
      </section>
      {/* Core Companies End */}

      <section className="section-sm" style={{ paddingTop: 0 }}><div className="container"><StatsRow /></div></section>

      <ContractingRows num="02" />

      <EngagementModels num="03" />

      {/* Why PMC Start */}
      <section className="section section-dark">
        <div className="aurora" /><div className="grid-bg" />
        <div className="container sticky-split" style={{ position: 'relative' }}>
          <div className="sticky-side">
            <SectionHead num="04" kicker="Why PMC matters" title="An independent PMC|*protects* your investment" lead="Here is what that means on your project, in practice." />
            <Reveal delay={0.2}><ArrowButton to="/request-consultation" variant="white">Discuss your project</ArrowButton></Reveal>
          </div>
          <Stagger className="num-rows" gap={0.08}>
            {WHY_PMC.map((t, i) => (
              <StaggerItem key={i} className="num-row"><b>0{i + 1}</b><p>{t}</p><Icon name="arrow" size={22} /></StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
      {/* Why PMC End */}

      {/* Values according Start */}
      <section className="section">
        <div className="container sticky-split">
          <div className="sticky-side">
            <SectionHead num="05" kicker="Core values" title="The six standards|we *work* by" lead="The measures behind every Blue Block engagement, from first site visit to final handover." />
          </div>
          <Reveal className="acc">
            {VALUES.map((v, i) => {
              const isOpen = open === i;
              return (
                <div key={v.title} className={`acc-item${isOpen ? ' open' : ''}`}>
                  <button className="acc-btn" onClick={() => setOpen(isOpen ? -1 : i)} aria-expanded={isOpen}>
                    <span className="icon-box"><Icon name={v.icon} size={22} /></span>
                    <h3>{v.title}</h3>
                    <span className="acc-plus"><Icon name="plus" size={18} /></span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div className="acc-body" initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.5, ease: EASE }}>
                        <p><strong style={{ color: 'var(--heading)' }}>{v.short}</strong> {v.desc}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </Reveal>
        </div>
      </section>
      {/* Values according End */}

      <CTABand title="Need a PMC or build|partner you can *trust?*" />
    </>
  );
}